import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, CardBody, Button } from '@img/ui';
import { api, ApiError, type ApiSuccess } from '../lib/api';

/**
 * 🔑 صفحة الانضمام لجمعية ملاك عبر رابط الدعوة
 * تستقبل التوكن من URL، تستدعي POST /api/associations/join/:token،
 * وتعرض حالة (تحميل / نجاح / خطأ / يحتاج تسجيل دخول).
 */
export function AssociationJoinPage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();

  type State =
    | { kind: 'loading' }
    | { kind: 'success'; associationId: string; message: string }
    | { kind: 'auth-required' }
    | { kind: 'error'; message: string };

  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setState({ kind: 'error', message: 'الرابط غير صالح' }); return; }
      try {
        const res = await api.post<ApiSuccess<{ associationId: string }>>(`/api/associations/join/${token}`, {});
        if (cancelled) return;
        setState({ kind: 'success', associationId: res.data.associationId, message: res.message ?? 'تم انضمامك للجمعية' });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 401) {
          // 🔐 يحتاج تسجيل دخول — احفظ الرابط ووجّهه لصفحة الدخول
          try { sessionStorage.setItem('postLoginRedirect', `/associations/join/${token}`); } catch { /* ignore */ }
          setState({ kind: 'auth-required' });
        } else {
          setState({ kind: 'error', message: e instanceof Error ? e.message : 'حدث خطأ غير متوقع' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-dark to-navy flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardBody>
          {state.kind === 'loading' && (
            <div className="text-center py-8">
              <div className="text-5xl mb-3">⟳</div>
              <h1 className="text-xl font-black text-navy-dark mb-2">جاري التحقق من الدعوة…</h1>
              <p className="text-sm text-gray-600">لحظات من فضلك</p>
            </div>
          )}

          {state.kind === 'success' && (
            <div className="text-center py-6">
              <div className="text-6xl mb-3">✅</div>
              <h1 className="text-2xl font-black text-navy-dark mb-2">{state.message}</h1>
              <p className="text-sm text-gray-600 mb-5">تم تفعيل عضويتك بنجاح. تقدر الحين تدخل صفحة الجمعية وتشارك في القرارات والتصويت.</p>
              <div className="flex gap-2">
                <Button variant="primary" className="flex-1" onClick={() => navigate('/dashboard/my-associations')}>جمعياتي</Button>
                <Button variant="ghost" onClick={() => navigate('/')}>الرئيسية</Button>
              </div>
            </div>
          )}

          {state.kind === 'auth-required' && (
            <div className="text-center py-6">
              <div className="text-6xl mb-3">🔐</div>
              <h1 className="text-2xl font-black text-navy-dark mb-2">يلزم تسجيل الدخول</h1>
              <p className="text-sm text-gray-600 mb-5">للانضمام للجمعية، سجّل دخول أو أنشئ حساباً جديداً. نرجعك لنفس الرابط بعد الدخول.</p>
              <div className="flex gap-2">
                <Button variant="primary" className="flex-1" onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/associations/join/${token}`)}`)}>تسجيل الدخول</Button>
                <Link to={`/register?redirect=${encodeURIComponent(`/associations/join/${token}`)}`} className="flex-1">
                  <Button variant="ghost" className="w-full">إنشاء حساب</Button>
                </Link>
              </div>
            </div>
          )}

          {state.kind === 'error' && (
            <div className="text-center py-6">
              <div className="text-6xl mb-3">⛔</div>
              <h1 className="text-2xl font-black text-navy-dark mb-2">تعذّر الانضمام</h1>
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-5">{state.message}</p>
              <p className="text-xs text-gray-500 mb-4">قد يكون الرابط منتهي الصلاحية أو تم استخدامه مسبقاً. تواصل مع مدير الجمعية لإعادة إرسال الدعوة.</p>
              <Button variant="primary" className="w-full" onClick={() => navigate('/')}>الرجوع للرئيسية</Button>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
